import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';

export default function ProviderApplyPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [applicationData, setApplicationData] = useState<any>(null);

  useEffect(() => {
    const loadApplicationData = () => {
      console.log('🔍 Loading application data...');
      console.log('Location state:', location.state);
      
      let data = null;

      // Try to get data from location state first
      if (location.state) {
        data = location.state;
        console.log('✅ Retrieved application data from location state:', data);
      } else {
        // Fallback to sessionStorage
        const storedData = sessionStorage.getItem('provider_application_data');
        console.log('SessionStorage data:', storedData);
        
        if (storedData) {
          try {
            data = JSON.parse(storedData);
            console.log('✅ Retrieved application data from sessionStorage:', data);
          } catch (err) {
            console.error('Failed to parse stored data:', err);
          }
        }
      }

      // Check if we have the required data
      if (!data || !data.email) {
        console.error('❌ Missing required data. Data received:', data);
        setError('Session expired. Please start your application again.');
        setLoading(false);
        setTimeout(() => {
          navigate('/provider-apply-trade-select');
        }, 2000);
        return;
      }

      console.log('✅ Application data loaded successfully:', {
        email: data.email,
        firstName: data.firstName,
        trade: data.selectedTrade
      });

      setApplicationData(data);
      setLoading(false);

      // Check if returning from successful payment
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      
      if (sessionId) {
        console.log('✅ Detected successful payment, creating account...');
        handlePaymentSuccess(data);
      }
    };

    loadApplicationData();
  }, [location, navigate]);

  const handlePaymentSuccess = async (data: any) => {
    setProcessing(true);
    setError('');

    try {
      console.log('🔐 Creating provider account...');

      // Create provider account
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            phone: data.phone,
            user_type: 'provider'
          }
        }
      });

      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error('Failed to create account');

      console.log('✅ Auth account created:', authData.user.id);

      // Create provider profile
      const { error: profileError } = await supabase
        .from('provider_profiles')
        .insert({
          user_id: authData.user.id,
          trade_type: data.selectedTrade,
          postcode: data.postcode,
          city: data.city,
          service_cities: data.serviceCities || [data.city],
          profile_photo_url: data.profilePhotoUrl,
          selfie_url: data.selfieUrl,
          subscription_status: 'active',
          subscription_paid_at: new Date().toISOString(),
          verification_status: 'pending',
          is_approved: false
        });

      if (profileError) throw profileError;

      console.log('✅ Provider profile created');

      // Clear stored data
      sessionStorage.removeItem('provider_application_data');

      // Sign in the user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      });

      if (signInError) throw signInError;

      console.log('✅ User signed in, redirecting to dashboard...');

      // Redirect to provider dashboard
      setTimeout(() => {
        navigate('/provider-dashboard');
      }, 1000);

    } catch (err: any) {
      console.error('❌ Account creation error:', err);
      setError(err.message || 'Failed to create account');
      setProcessing(false);
    }
  };

  const handlePayment = async () => {
    if (!applicationData) {
      setError('Application data not found');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('💳 Creating Stripe checkout session...');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-subscription-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            email: applicationData.email,
            trade_type: applicationData.selectedTrade,
            success_url: `${window.location.origin}/provider-apply-payment?session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: window.location.href
          })
        }
      );

      const result = await response.json();

      if (!response.ok || !result.url) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      console.log('✅ Checkout session created, redirecting to Stripe...');

      // Redirect to Stripe checkout
      window.location.href = result.url;

    } catch (err: any) {
      console.error('❌ Payment error:', err);
      setError(err.message || 'Failed to process payment');
      setLoading(false);
    }
  };

  if (loading && !applicationData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-4xl text-teal-600 mb-4"></i>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (processing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-4xl text-teal-600 mb-4"></i>
          <p className="text-lg font-semibold text-gray-900 mb-2">Creating your account...</p>
          <p className="text-sm text-gray-600">Please wait while we set everything up</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-red-200 p-8 text-center">
          <i className="ri-error-warning-line text-5xl text-red-600 mb-4"></i>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/provider-apply-trade-select')}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold whitespace-nowrap cursor-pointer"
          >
            Start New Application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-bold text-slate-900">Complete Your Subscription</h1>
          <p className="text-sm text-slate-600 mt-1">
            Final step to join Taskbudy's provider network
          </p>
        </div>
      </header>

      {/* Payment Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          {/* Application Summary */}
          <div className="mb-8 pb-8 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Application Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-semibold text-gray-900">
                  {applicationData?.firstName} {applicationData?.lastName}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold text-gray-900">{applicationData?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Trade</p>
                <p className="font-semibold text-gray-900 capitalize">
                  {applicationData?.selectedTrade?.replace('_', ' ')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Service Area</p>
                <p className="font-semibold text-gray-900">
                  {applicationData?.serviceCities?.length || 1} {applicationData?.serviceCities?.length === 1 ? 'city' : 'cities'}
                </p>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border-2 border-teal-500 mb-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Annual Subscription</h3>
              <p className="text-gray-600">Join Taskbudy's network of professional tradespeople</p>
            </div>
            
            <div className="text-center mb-6">
              <div className="text-5xl font-bold text-teal-600 mb-2">£378</div>
              <div className="text-sm text-gray-600 mb-4">inc. VAT</div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Save 25% on your first year
              </div>
              <div className="text-sm text-gray-600">
                Then £35/month (£420 + VAT/year)
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 space-y-3">
              <h4 className="font-bold text-gray-900 mb-4">What's included:</h4>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-teal-600 text-xl"></i>
                <span className="text-gray-700">Unlimited job access</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-teal-600 text-xl"></i>
                <span className="text-gray-700">Priority support</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-teal-600 text-xl"></i>
                <span className="text-gray-700">Professional profile</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-teal-600 text-xl"></i>
                <span className="text-gray-700">Instant payouts available</span>
              </div>
            </div>
          </div>

          {/* Payment Button */}
          <button
            onClick={handlePayment}
            disabled={loading}
            className="w-full px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-bold text-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Processing...
              </>
            ) : (
              <>
                Pay £378 & Complete Application
                <i className="ri-arrow-right-line ml-2"></i>
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            Secure payment powered by Stripe. Your payment information is encrypted and secure.
          </p>
        </div>
      </div>
    </div>
  );
}
